import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';

export interface SyncAccount {
  id: number;
  provider: 'google' | 'microsoft';
  external_email: string;
  is_active: boolean;
  last_sync_at: number | null;
  has_cursor: boolean;
  created_at: number;
}

export interface SyncProvider {
  name: 'google' | 'microsoft';
  label: string;
  /** Whether the operator has set this provider's OAuth credentials. */
  configured: boolean;
}

export function useSyncAccounts() {
  return useQuery({
    queryKey: ['calendar-sync-accounts'],
    queryFn: ({ signal }) =>
      api.get<{ accounts: SyncAccount[] }>('/api/calendar-sync/accounts', signal),
  });
}

export function useSyncProviders() {
  return useQuery({
    queryKey: ['calendar-sync-providers'],
    queryFn: ({ signal }) =>
      api.get<{ providers: SyncProvider[] }>('/api/calendar-sync/providers', signal),
    staleTime: 5 * 60 * 1000,
  });
}

export function useToggleSyncAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      api.patch<{ account: SyncAccount }>(`/api/calendar-sync/accounts/${id}`, { is_active }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['calendar-sync-accounts'] });
    },
  });
}

export function useDisconnectSyncAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/calendar-sync/accounts/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['calendar-sync-accounts'] });
    },
  });
}

/**
 * Begin the OAuth consent flow.
 *
 * A mutation followed by a manual navigation, rather than an <a href> to the
 * API: the access token lives in memory and rides an Authorization header,
 * which a link click cannot send, so a navigated /api/oauth/... start route
 * arrives unauthenticated and 401s. The app makes the authenticated request,
 * the server hands back the provider URL, and only then do we leave the page.
 * Same constraint as the Bearer-only byte endpoints in frontend-patterns.md
 * section 5.5.
 */
export function useStartCalendarOAuth() {
  return useMutation({
    mutationFn: (provider: SyncProvider['name']) =>
      api.post<{ url: string }>(`/api/oauth/${provider}/start`),
  });
}
